import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { LockClosedIcon } from '@heroicons/react/20/solid';
import { ViewColumnsIcon } from '@heroicons/react/24/outline';
import { SecondaryButtonClassname } from '@shared/components/buttons';
import type { SortingState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useEditVisibleColumns } from '../hooks/use-visible-columns';
import { Popover } from './popover/popover';
import { SortableColumnRow } from './sortable-column-row';
import { SortIndicator } from './tanstack-table/sort-indicator';
import { PopoverHeader } from './toolbar/popover-header';

export function EditColumns({
  tableKey,
  pinnedColumns,
  defaultHiddenColumnIds,
  sorting,
  sortOnlyColumns,
}: {
  tableKey: string;
  pinnedColumns?: string[];
  defaultHiddenColumnIds?: string[];
  /**
   * Active table sort. When provided, sorted columns get a sort mark in the
   * selector — so a column that drives the sort stays discoverable even while
   * it is hidden from the table itself.
   */
  sorting?: SortingState;
  /**
   * Columns that exist only to drive the sort and are never shown in the
   * table. Listed at the bottom of the selector as locked rows so their place
   * in the sort order stays visible.
   */
  sortOnlyColumns?: { id: string; label: string }[];
}) {
  const { columnStates, toggleColumn, reorderColumns, resetColumns, isDefault } =
    useEditVisibleColumns(tableKey, pinnedColumns, defaultHiddenColumnIds);

  // Column id → its place in the multi-sort. Order numbers only earn their
  // place when more than one column is sorted (matches the table header).
  const sortByColumnId = useMemo(() => {
    const isMultiSort = (sorting?.length ?? 0) > 1;
    const map = new Map<string, { direction: 'asc' | 'desc'; order?: number }>();
    sorting?.forEach((entry, index) => {
      map.set(entry.id, {
        direction: entry.desc ? 'desc' : 'asc',
        order: isMultiSort ? index + 1 : undefined,
      });
    });

    return map;
  }, [sorting]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const ids = columnStates.map((c) => c.id);
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      reorderColumns(arrayMove(ids, oldIndex, newIndex));
    }
  };

  return (
    <Popover>
      <Popover.Trigger>
        <button
          type="button"
          className={`${SecondaryButtonClassname} h-10 flex items-center justify-center`}
        >
          <span className="text-icon">
            <ViewColumnsIcon className="size-5" />
          </span>
          <span>Columns</span>
        </button>
      </Popover.Trigger>
      <Popover.Content className="min-w-56" align="end">
        <PopoverHeader
          label="Columns"
          action={{ label: 'Reset', onClick: resetColumns, disabled: isDefault }}
        />
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={columnStates.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            {columnStates.map((column) => (
              <SortableColumnRow
                key={column.id}
                column={column}
                sort={sortByColumnId.get(column.id)}
                onToggle={toggleColumn}
              />
            ))}
          </SortableContext>
        </DndContext>
        {sortOnlyColumns && sortOnlyColumns.length > 0 && (
          <div className="mt-1 border-t border-gray-200 pt-1">
            {sortOnlyColumns.map((column) => {
              const sort = sortByColumnId.get(column.id);

              return (
                <div key={column.id} className="flex flex-row items-center gap-2 p-1">
                  <LockClosedIcon className="size-4 shrink-0 text-gray-400" />
                  <span className="flex-1 select-none text-sm text-gray-500">{column.label}</span>
                  {sort && <SortIndicator direction={sort.direction} order={sort.order} />}
                </div>
              );
            })}
          </div>
        )}
      </Popover.Content>
    </Popover>
  );
}
